import Modal from "@mui/material/Modal";
import React from "react";
import styled from "styled-components";
import { Cancel } from "../../../../../assets/icons";
import { colors } from "../../../../../utils";
import { device } from "../../../../../utils/device";
import Button from "../../../../Button";
import TextView from "../../../../TextView";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
} from "@mui/material";
import { ButtonLoader } from "../../../../inputs.styled";
import ProSuite from "@assets/images/ProSuite.svg";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  text-align: left;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  max-width: 500px;
  width: 90%;

  @media ${device.mobilePortrait} {
    width: 95%;
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.lightBorder};
`;

const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
`;

const CloseIcon = styled.div`
  position: absolute;
  right: 24px;
  top: 24px;
  cursor: pointer;
  width: 24px;
`;

const Content = styled.div`
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  justify-content: flex-end;
  grid-gap: 10px;

  @media ${device.mobilePortrait} {
    flex-direction: column;
  }
`;

const IconImg = styled.img`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const BusinessBoxPermissionDialog = ({
  isOpen,
  onClose,
  onApply,
  onCancel,
  icon,
  title = "Would you like this Business Box to apply to:",
  description = "",
  options = [
    { value: "gallery", label: "Gallery only" },
    { value: "account", label: "Account-wide" },
  ],
  defaultValue = "gallery",
  applyButtonText = "Apply",
  cancelButtonText = "Cancel",
  apiLoading,
  handleDisableIconClick,
}) => {
  const [selectedValue, setSelectedValue] = React.useState(defaultValue);

  // Reset selection when modal opens or default changes
  React.useEffect(() => {
    setSelectedValue(defaultValue);
  }, [isOpen, defaultValue]);

  const handleApply = () => {
    onApply(selectedValue);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    onClose();
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="business-box-permission-dialog"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <ModalContainer>
        <CloseIcon>
          <Cancel onClick={onClose} />
        </CloseIcon>

        <Header>
          {icon && (
            <IconWrapper>
              <img src={icon} alt="Dialog Icon" />
            </IconWrapper>
          )}
          <Title>{title}</Title>
        </Header>

        <Content>
          {!!description && <TextView>{description}</TextView>}
          <FormControl component="fieldset">
            <RadioGroup
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  disabled={option?.disabled && option.disabled}
                  control={
                    <Radio
                      sx={{
                        color: "#BBBBBB",
                        "&.Mui-checked": {
                          color: "#9B5AF8",
                        },
                      }}
                    />
                  }
                  label={
                    option?.disabled ? (
                      <LabelWithIcon>
                        {option.label}
                        <IconImg
                          src={ProSuite}
                          alt="ProSuite"
                          onClick={handleDisableIconClick}
                        />
                      </LabelWithIcon>
                    ) : (
                      option.label
                    )
                  }
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Content>

        <ButtonContainer>
          <Button variant="secondary" onClick={handleCancel}>
            {cancelButtonText}
          </Button>
          <Button onClick={handleApply}>
            {apiLoading && <ButtonLoader />} {applyButtonText}
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </StyledModal>
  );
};

export default BusinessBoxPermissionDialog;
